import React, { useCallback, useEffect, useRef, useState } from 'react';
import axios from 'axios';
import { Check, ChevronsUpDown, User } from 'lucide-react';
import { API_URL } from '../config/api';
import { useTranslation } from '../context/TranslationContext';
import { formatPersonName } from '../lib/formatPersonName';
import { Button } from './ui/button';
import { Popover, PopoverContent, PopoverTrigger } from './ui/popover';
import { Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList } from './ui/command';

const SEARCH_DEBOUNCE_MS = 300;
const PAGE_SIZE = 40;

const ContactSearchPicker = ({
  selectedContact,
  onSelect,
  className = '',
  buttonClassName = '',
  size = 'default',
}) => {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const [contacts, setContacts] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const debounceRef = useRef(null);
  const requestIdRef = useRef(0);

  const fetchContacts = useCallback(async (query) => {
    const requestId = ++requestIdRef.current;
    try {
      setLoading(true);
      const params = { limit: PAGE_SIZE, offset: 0 };
      const trimmed = (query || '').trim();
      if (trimmed) {
        params.q = trimmed;
      }
      const response = await axios.get(`${API_URL}/api/contacts`, {
        params,
        headers: {
          Authorization: `Bearer ${localStorage.getItem('access_token')}`,
        },
      });
      if (requestId === requestIdRef.current) {
        setContacts(response.data || []);
      }
    } catch (error) {
      console.error('Error fetching contacts:', error);
      if (requestId === requestIdRef.current) {
        setContacts([]);
      }
    } finally {
      if (requestId === requestIdRef.current) {
        setLoading(false);
      }
    }
  }, []);

  useEffect(() => {
    if (!open) return undefined;

    if (debounceRef.current) {
      clearTimeout(debounceRef.current);
    }

    // Immediate fetch when opening / empty query; debounce while typing
    const delay = searchQuery.trim() ? SEARCH_DEBOUNCE_MS : 0;
    debounceRef.current = setTimeout(() => {
      fetchContacts(searchQuery);
    }, delay);

    return () => {
      if (debounceRef.current) {
        clearTimeout(debounceRef.current);
      }
      requestIdRef.current += 1;
    };
  }, [open, searchQuery, fetchContacts]);

  const displayValue = selectedContact?.name
    ? formatPersonName(selectedContact.name)
    : selectedContact?.first_name || selectedContact?.last_name
      ? formatPersonName(`${selectedContact.first_name || ''} ${selectedContact.last_name || ''}`.trim())
      : '';

  const handleSelect = (contact) => {
    onSelect(contact);
    setOpen(false);
  };

  const handleOpenChange = (nextOpen) => {
    setOpen(nextOpen);
    if (!nextOpen) {
      setSearchQuery('');
    }
  };

  const isCompact = size === 'sm';

  return (
    <Popover open={open} onOpenChange={handleOpenChange}>
      <PopoverTrigger asChild>
        <Button
          variant="outline"
          role="combobox"
          aria-expanded={open}
          className={`w-full justify-between ${isCompact ? 'h-9 text-sm' : 'h-11 text-base'} ${buttonClassName}`}
        >
          <span className="truncate text-left">
            {displayValue || t('configurator.searchContacts')}
          </span>
          <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
        </Button>
      </PopoverTrigger>
      <PopoverContent className={`p-0 ${className}`} style={{ width: 'var(--radix-popover-trigger-width)' }}>
        {/* shouldFilter=false: filtering is done server-side */}
        <Command shouldFilter={false}>
          <CommandInput
            placeholder={t('configurator.searchContacts')}
            className={isCompact ? 'h-9 text-xs' : 'h-10 text-sm'}
            value={searchQuery}
            onValueChange={setSearchQuery}
          />
          <CommandList>
            <CommandEmpty>
              {loading ? t('configurator.loadingContacts') : t('configurator.noContactsFound')}
            </CommandEmpty>
            <CommandGroup>
              {contacts.map((contact) => {
                const fullName = formatPersonName(`${contact.first_name} ${contact.last_name}`.trim());
                const displayText = `${fullName}${contact.email ? ` - ${contact.email}` : ''}${contact.phone ? ` - ${contact.phone}` : ''}`;
                const isSelected = selectedContact?.id === contact.id;

                return (
                  <CommandItem
                    key={contact.id}
                    value={displayText}
                    onSelect={() => handleSelect(contact)}
                    className={`${isCompact ? 'text-xs' : 'text-sm'} cursor-pointer`}
                  >
                    <Check className={`mr-2 h-4 w-4 ${isSelected ? 'opacity-100' : 'opacity-0'}`} />
                    <div className="flex items-center gap-2 min-w-0">
                      <User className="h-4 w-4 shrink-0 text-gray-400" />
                      <div className="min-w-0">
                        <div className="font-medium truncate">{fullName}</div>
                        {(contact.email || contact.phone) && (
                          <div className="text-xs text-gray-500 truncate">
                            {contact.email && <span>{contact.email}</span>}
                            {contact.email && contact.phone && <span> • </span>}
                            {contact.phone && <span>{contact.phone}</span>}
                          </div>
                        )}
                      </div>
                    </div>
                  </CommandItem>
                );
              })}
            </CommandGroup>
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
};

export default ContactSearchPicker;
